import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'お問い合わせ｜岡山市の印刷会社 おかやま印刷株式会社',
    description: 'おかやま印刷株式会社へのお問い合わせはこちらから。お見積もりやご相談など、お気軽にお問い合わせください。電話、FAX、お問い合わせフォームで受付しております。',
    keywords: 'お問い合わせ,見積もり,相談,印刷会社,岡山市,おかやま印刷',
    canonical: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'お問い合わせ',
      description: 'おかやま印刷株式会社へのお問い合わせページ。お見積もりやご相談など、お気軽にお問い合わせください。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'おかやま印刷株式会社',
        telephone: '+81-86-012-3456',
        faxNumber: '+81-86-012-3457',
      },
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d62237qbl2vbmmc7am1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary via-[#FF7A30] to-[#E85A20]"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              お問い合わせ
            </h1>
            <p className="text-xl md:text-2xl text-white drop-shadow-md">
              お気軽にご相談ください
            </p>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  お問い合わせ内容をご記入ください
                </h2>
                <p className="text-gray-600">
                  お見積もりやご相談など、お気軽にお問い合わせください。<br />
                  2営業日以内にご返信いたします。
                </p>
              </div>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-base font-bold text-gray-900 mb-2">
                    お名前 <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-base font-bold text-gray-900 mb-2">
                    会社名・団体名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="株式会社○○"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-base font-bold text-gray-900 mb-2">
                    電話番号 <span className="text-primary">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-base font-bold text-gray-900 mb-2">
                    メールアドレス <span className="text-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-base font-bold text-gray-900 mb-2">
                    お問い合わせ内容 <span className="text-primary">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.message.length} / 500文字
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl">
                    <p className="font-bold">送信完了</p>
                    <p className="text-sm">お問い合わせありがとうございます。2営業日以内にご返信いたします。</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
                    <p className="font-bold">送信エラー</p>
                    <p className="text-sm">送信に失敗しました。お手数ですが、お電話でお問い合わせください。</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-primary text-white rounded-full font-bold text-lg hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line text-xl animate-spin"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-xl"></i>
                      送信する
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="py-16 md:py-24 bg-primary-light">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                その他のお問い合わせ方法
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-3xl p-8 shadow-lg">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-phone-line text-3xl text-primary"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">お電話でのお問い合わせ</h3>
                  <a
                    href="tel:086-012-3456"
                    className="text-3xl font-bold text-primary hover:text-primary-dark transition-colors mb-4 block cursor-pointer"
                  >
                    086-012-3456
                  </a>
                  <div className="space-y-2 text-gray-600">
                    <p className="flex items-center gap-2">
                      <i className="ri-time-line text-primary"></i>
                      営業時間：9:00 - 18:00
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-calendar-close-line text-primary"></i>
                      定休日：日曜日・祝日
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-lg">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-printer-line text-3xl text-primary"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">FAXでのお問い合わせ</h3>
                  <p className="text-3xl font-bold text-primary mb-4">086-012-3457</p>
                  <p className="text-gray-600">
                    24時間受付しております。<br />
                    お名前、ご連絡先、お問い合わせ内容をご記入ください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
